import { lazy, Suspense, useEffect } from 'react';
import { applyLandingMetadata } from './landingSeo';
import { currentLandingFaq, landingCopy, rentalLanding } from './landingRental';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import MidLockup from '../../components/MidLockup';
import MidMascot from '../../components/MidMascot';
import { NEW_BRAND } from '../../lib/brand';
import ZoningPlan from './ZoningPlan';
import LandingFooter from './LandingFooter';
import './landing.css';

// Browser-only maps: its own chunk, so public HTML renders without it and the landing never waits for it.
const LandingChat = lazy(() => import('./LandingChat'));

function LandingStartPair({ leadIn }) {
  return (
    <div className="landing-start">
      {leadIn && <p className="landing-start-label">{leadIn}</p>}
      <div className="landing-start-links">
        {rentalLanding() ? (
          <>
            <Link className="landing-button landing-button-primary" to="/comecar?from=comprar">Quero comprar casa</Link>
            <Link className="landing-button landing-button-outline" to="/comecar?intent=arrendamento">Quero arrendar casa</Link>
          </>
        ) : (
          <>
            <Link className="landing-button landing-button-primary" to="/comecar?intent=primeira_habitacao">Vou comprar a primeira casa</Link>
            <Link className="landing-button landing-button-outline" to="/comecar?intent=upgrade">Quero trocar de casa</Link>
          </>
        )}
      </div>
    </div>
  );
}

export default function LandingHome({ landingChatEnabled = false }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const copy = landingCopy();

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'agent') navigate('/agent/dashboard', { replace: true });
    else if (isAuthenticated && user?.user_type === 'admin') navigate('/admin', { replace: true });
  }, [isAuthenticated, user, navigate]);

  useEffect(() => applyLandingMetadata('/'), []);

  return (
    <div className={`landing-home${landingChatEnabled ? ' landing-home-with-chat' : ''}`} data-testid="landing-home">
      <header className="landing-header">
        <div className="landing-wrap landing-header-inner">
          <Link to="/" aria-label="miD, página inicial"><MidLockup variant="neutro" fontSize={36} animated={false} color="var(--brand-ink, #FAFAF7)" tone="auto" /></Link>
          {isAuthenticated && user?.user_type === 'buyer'
            ? <Link to="/buyer/dashboard">Ir para o meu mapa</Link>
            : <Link to="/login">Entrar</Link>}
        </div>
      </header>
      <main>
        <section className="landing-hero landing-dark">
          <div className="landing-wrap landing-hero-grid">
            <div className="landing-hero-copy">
              {NEW_BRAND && <p className="landing-eyebrow"><span aria-hidden="true" className="landing-eyebrow-mark"><i /><i /><i /></span>A planta da tua procura</p>}
              <h1>{copy.h1}</h1>
              <p className="landing-hero-subtitle">{copy.subtitle}</p>
              <LandingStartPair leadIn="Por onde começas?" />
              <p className="landing-support">{copy.support}</p>
            </div>
            <aside className="landing-example" aria-label="Exemplo ilustrativo de resultado do Matchmaker">
              {NEW_BRAND && <ZoningPlan />}
              <p className="landing-example-label">Exemplo ilustrativo · Matchmaker</p>
              <h2>T2 com varanda em Lisboa</h2>
              <p className="landing-example-score">78% <span>{copy.exampleMatch}</span></p>
              <ul>
                <li>Encaixa: a tipologia e a varanda que procuras.</li>
                <li>A confirmar: o tempo de viagem até ao trabalho.</li>
              </ul>
              <p>Vês os motivos para comparar antes de marcar uma visita.</p>
            </aside>
          </div>
        </section>
        <section className="landing-section">
          <div className="landing-wrap">
            <h2>{copy.stepsTitle}</h2>
            <div className="landing-steps">
              <article className="landing-step">
                <div aria-hidden="true"><MidMascot state="thinking" size={56} /></div>
                <p className="landing-step-label">{copy.step1Label}</p>
                <h3>{copy.step1Title}</h3>
                <p>Conversas sobre quem vai viver contigo, a tipologia e o orçamento. Defines as zonas, a tua rotina e aquilo de que não abdicas, com escolhas concretas: mais espaço ou uma zona melhor?</p>
                <p className="landing-step-result"><strong>Ficas com:</strong>{copy.step1Result}</p>
              </article>
              <article className="landing-step">
                <div aria-hidden="true"><MidMascot state="reading" size={56} /></div>
                <p className="landing-step-label">2 · Matchmaker</p>
                <h3>Vê se uma casa te serve</h3>
                <p>{copy.step2Body}</p>
                <p className="landing-step-result"><strong>Ficas com:</strong>{copy.step2Result}</p>
              </article>
              <article className="landing-step">
                <div aria-hidden="true"><MidMascot state="midStatus" size={56} /></div>
                <p className="landing-step-label">{copy.step3Label}</p>
                <h3>{copy.step3Title}</h3>
                <p>{copy.step3Body}</p>
                <p className="landing-step-result"><strong>Ficas com:</strong>{copy.step3Result}</p>
              </article>
            </div>
            <LandingStartPair leadIn="Começa pelo passo 1. Escolhe o teu caso:" />
          </div>
        </section>
        <section className="landing-section landing-trust">
          <div className="landing-wrap">
            <h2>A tua procura. As tuas escolhas.</h2>
            <p className="landing-section-intro">Os portais organizam as casas. O miD organiza quem as procura.</p>
            <div className="landing-trust-items">
              <article>
                <h3>Traz os anúncios que já encontraste</h3>
                <p>{copy.trustListings}</p>
              </article>
              <article>
                <h3>Escolhe quando falar com um agente</h3>
                <p>O teu email e telemóvel ficam privados. Só conversas com um agente no miD depois de aceitares um match ou pedires uma visita.</p>
              </article>
              <article>
                <h3>{copy.trustMatch}</h3>
                <p>Os agentes podem pagar por ferramentas de trabalho, nunca por um Match melhor nem por um lugar mais alto na tua lista.</p>
              </article>
            </div>
            <LandingStartPair leadIn="Escolhe o teu caso e começa:" />
          </div>
        </section>
        <section className="landing-section landing-faq">
          <div className="landing-wrap">
            <h2>{copy.faqTitle}</h2>
            <div className="landing-faq-items">
              {currentLandingFaq().map(({ question, answer }) => (
                <details key={question}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
        <section className="landing-section landing-dark landing-final">
          <div className="landing-wrap">
            <h2>Primeiro o plano. Depois as visitas.</h2>
            <p>{copy.finalBody}</p>
            <LandingStartPair />
          </div>
        </section>
      </main>
      <LandingFooter />
      {/* G-01 SLOT: floating mascot entry (desktop: the DNA chat panel, onep.20; mobile: /comecar).
          Fixed bottom-right: right 16px, bottom calc(16px + env(safe-area-inset-bottom)).
          Above page content, below modals and the cookie banner while that is open.
          G-01 adds the bottom padding the page needs so the button never covers footer links. */}
      <div data-testid="landing-chat-slot">{landingChatEnabled && <Suspense fallback={null}><LandingChat /></Suspense>}</div>
    </div>
  );
}
